import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import notification from "../assets/notification.mp3";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    // Load preference from localStorage, default to true
    const saved = localStorage.getItem('notificationSound');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  // Store previous unread count to detect new notifications
  const prevUnreadCountRef = useRef(unreadCount);
  const audioRef = useRef(null);
  const lastNotificationTimeRef = useRef(0);

  // Initialize audio on component mount (required for Chrome)
  useEffect(() => {
    // Create audio element
    audioRef.current = new Audio(notification); // You'll need to add this file
    
    // Preload audio (helps with playback on Chrome)
    audioRef.current.load();
    
    // Enable audio to work without user interaction (Chrome requires user interaction first)
    const enableAudio = () => {
      if (audioRef.current) {
        audioRef.current.play().catch(() => {}).then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        });
      }
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };
    
    document.addEventListener('click', enableAudio);
    document.addEventListener('touchstart', enableAudio);
    
    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };
  }, []);

  // Alternative: Create a beep sound using Web Audio API (no file needed)
  const playBeepSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Resume audio context if suspended (Chrome autoplay policy)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.5);
      
      return true;
    } catch (error) {
      console.log('Web Audio API not supported or blocked');
      return false;
    }
  };

  // Play notification sound function
  const playNotificationSound = () => {
    if (!soundEnabled) return;
    
    // Prevent sound spam (minimum 2 seconds between sounds)
    const now = Date.now();
    if (now - lastNotificationTimeRef.current < 2000) {
      return;
    }
    
    lastNotificationTimeRef.current = now;
    
    // Try to play audio file first
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play()
        .catch(() => {
          // Fallback to Web Audio API beep if file fails
          playBeepSound();
        });
    } else {
      // Fallback to Web Audio API beep
      playBeepSound();
    }
  };

  const createNotification = async (notificationData) => {
    try {
      const response = await api.post('/notifications', notificationData);
      return response.data;
    } catch (err) {
      console.error('Error creating notification:', err);
      throw err;
    }
  };

  const fetchNotifications = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const response = await api.get('/notifications');
      const newNotifications = response.data.notifications;
      const newUnreadCount = response.data.unreadCount;
      
      // Check if there are new unread notifications
      if (newUnreadCount > prevUnreadCountRef.current) {
        playNotificationSound();
      }
      
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
      prevUnreadCountRef.current = newUnreadCount;
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => {
        const newCount = prev > 0 ? prev - 1 : 0;
        prevUnreadCountRef.current = newCount;
        return newCount;
      });
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      prevUnreadCountRef.current = 0;
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  // Toggle sound on/off
  const toggleSound = () => {
    setSoundEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('notificationSound', JSON.stringify(newValue));
      return newValue;
    });
  };

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Also check for new notifications when window gains focus
  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        fetchNotifications();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        dropdownOpen,
        setDropdownOpen,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        createNotification,
        soundEnabled,
        toggleSound
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);